import { BASE_API_URL } from "../api";
import { REQUEST_CONTEXT } from "./clientPortalConstants";
import { ClientPortalHttpError } from "./clientPortalErrors";

export const CLIENT_PORTAL_ENDPOINTS = Object.freeze({
  token: "auth/token/",
  refresh: "auth/token/refresh/",
  registration: "client/requestCreate/",
  registrationCounties: "county/?orderBy_Name=true",
  registrationCities: "city/",
  whoami: "user/whoami/",
  logout: "user/logout/",
  client: "client/",
  clientQR: "client/getQR/",
  promotions: "promotion/",
  claims: "promotion/listClaims/",
  claim: "promotion/claim/",
  directBenefits: "promotiondirect/",
  news: "news/",
  orderAdd: "orders/add/",
  orderRegisterPay: "orders/registerPay/",
  orderPayStatus: "orders/getPayStatus/",
  orderCancel: "orders/cancelOrder/",
});

function endpointUrl(path) {
  const value = String(path || "");
  if (!value || /^https?:\/\//i.test(value) || value.startsWith("//")) {
    throw new Error("Invalid client portal endpoint");
  }
  return `${BASE_API_URL}${value.replace(/^\/+/, "")}`;
}

export function buildRequestHeaders(context, token, hasBody = false) {
  const headers = { Accept: "application/json" };
  if (hasBody) headers["Content-Type"] = "application/json";

  if (context !== REQUEST_CONTEXT.public && token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

export async function clientPortalRequest(
  path,
  {
    method = "GET",
    body,
    context = REQUEST_CONTEXT.public,
    token = "",
    signal,
  } = {}
) {
  const hasBody = body !== undefined;
  let response;
  try {
    response = await fetch(endpointUrl(path), {
      method,
      headers: buildRequestHeaders(context, token, hasBody),
      ...(hasBody ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
      cache: "no-store",
    });
  } catch (error) {
    throw new ClientPortalHttpError(0);
  }

  let text;
  try {
    text = await response.text();
  } catch (error) {
    throw new ClientPortalHttpError(0);
  }
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch (error) {
      data = null;
    }
  }

  if (!response.ok) {
    throw new ClientPortalHttpError(response.status, data);
  }
  return data;
}

const publicPost = (path, body) =>
  clientPortalRequest(path, {
    method: "POST",
    body,
    context: REQUEST_CONTEXT.public,
  });

const clientGet = (path, accessToken) =>
  clientPortalRequest(path, {
    context: REQUEST_CONTEXT.client,
    token: accessToken,
  });

export const clientPortalApi = {
  login: (username, password) =>
    publicPost(CLIENT_PORTAL_ENDPOINTS.token, { username, password }),
  refresh: (refresh) =>
    publicPost(CLIENT_PORTAL_ENDPOINTS.refresh, { refresh }),
  getRegistrationCounties: () =>
    clientPortalRequest(CLIENT_PORTAL_ENDPOINTS.registrationCounties, {
      context: REQUEST_CONTEXT.public,
    }),
  getRegistrationCities: (countyId) =>
    clientPortalRequest(
      `${CLIENT_PORTAL_ENDPOINTS.registrationCities}?county=${encodeURIComponent(
        String(countyId || "").trim()
      )}&orderBy_Name=true`,
      {
        context: REQUEST_CONTEXT.public,
      }
    ),
  registerClientAccount: (payload) =>
    publicPost(CLIENT_PORTAL_ENDPOINTS.registration, payload),
  whoami: (accessToken) => clientGet(CLIENT_PORTAL_ENDPOINTS.whoami, accessToken),
  logout: (accessToken, refresh) =>
    clientPortalRequest(CLIENT_PORTAL_ENDPOINTS.logout, {
      method: "POST",
      body: { refresh },
      context: REQUEST_CONTEXT.client,
      token: accessToken,
    }),
  getClient: (accessToken) => clientGet(CLIENT_PORTAL_ENDPOINTS.client, accessToken),
  getQR: (accessToken) => clientGet(CLIENT_PORTAL_ENDPOINTS.clientQR, accessToken),
  getPromotions: (accessToken) =>
    clientGet(CLIENT_PORTAL_ENDPOINTS.promotions, accessToken),
  getClaims: (accessToken) => clientGet(CLIENT_PORTAL_ENDPOINTS.claims, accessToken),
  claimPromotion: (accessToken, promotion) =>
    clientPortalRequest(CLIENT_PORTAL_ENDPOINTS.claim, {
      method: "POST",
      body: { promotion },
      context: REQUEST_CONTEXT.client,
      token: accessToken,
    }),
  getDirectBenefits: (accessToken) =>
    clientGet(CLIENT_PORTAL_ENDPOINTS.directBenefits, accessToken),
  getNews: (accessToken) => clientGet(CLIENT_PORTAL_ENDPOINTS.news, accessToken),
  addAuthenticatedOrder: (accessToken, payload) =>
    clientPortalRequest(CLIENT_PORTAL_ENDPOINTS.orderAdd, {
      method: "POST",
      body: payload,
      context: REQUEST_CONTEXT.client,
      token: accessToken,
    }),
  registerAuthenticatedPay: (accessToken, orderId) =>
    clientPortalRequest(CLIENT_PORTAL_ENDPOINTS.orderRegisterPay, {
      method: "POST",
      body: { orderId },
      context: REQUEST_CONTEXT.client,
      token: accessToken,
    }),
  getAuthenticatedPayStatus: (accessToken, orderId) =>
    clientGet(
      `${CLIENT_PORTAL_ENDPOINTS.orderPayStatus}?iref=${encodeURIComponent(
        orderId
      )}`,
      accessToken
    ),
  cancelAuthenticatedOrder: (accessToken, orderId) =>
    clientPortalRequest(CLIENT_PORTAL_ENDPOINTS.orderCancel, {
      method: "POST",
      body: { iref: orderId },
      context: REQUEST_CONTEXT.client,
      token: accessToken,
    }),
};
